import React, { useEffect, FC } from 'react';
import { graphql } from 'gatsby';
import { trackCustomEvent } from 'gatsby-plugin-google-analytics';
import { useTranslation, Trans } from 'react-i18next';

import SEO from '@components/seo';
import SideBar from '@components/sideBar/layout';
import SideBanner from '@components/sideBar/banner';
import RecommendedPosts from '@components/sideBar/recommended';
import SnsBanner from '@components/snsBanner';
import NewPosts from '@components/newPosts';
import CategoryTag from '@components/tags/category';
import PostBanner from '@components/postBanner';
import BorderCard from '@components/cards/border';
import ContentSlice from '@slices/content';
import IntervieweeSlice from '@slices/interviewee';
import Image from '@components/imgComp';

import usePreviewData from '../utils/usePreviewData';
import formatDate from '@utils/formatDate';
import authorData from '@config/authors';

import * as styles from './post.module.scss';
import FixedCta from '../components/fixedCta';

const transLink = (href: string) => <a href={href} target="_blank" rel="noreferrer" />;

const PostTemplate: FC<any> = ({ data, location, pageContext }) => {
    const { t } = useTranslation();

    const liveData = usePreviewData(data);
    if (!liveData) return null;

    useEffect(() => {
        if (data?.prismicPost) {
            trackCustomEvent({
                category: 'Article Viewed',
                action: 'Pageview',
                label: pageContext?.uid,
            });
        }
    }, []);

    const getPostData = () => {
        // if preview then return prismicPost, else display published content
        if (liveData.prismicPost) return { ...liveData.prismicPost };
        return data.prismicPost;
    };

    const getDescription = () => {
        return getPostData()?.data?.body.filter(({ slice_type }: { slice_type: string }) => slice_type === 'content')[0]
            ?.primary.text.text;
    };

    return (
        <>
            <SEO description={getDescription()} {...getPostData()} {...location} isPost />
            <FixedCta />
            <div className={styles['post_wrapper']}>
                <h1 style={{ color: getPostData()?.data?.title_color }}>{getPostData()?.data?.title?.text}</h1>
                <div className={styles['post_header']}>
                    <CategoryTag category={getPostData()?.data?.category} />
                    <span>{formatDate(getPostData()?.data?.date)}</span>
                    <span>by {getPostData()?.data?.author}</span>
                </div>

                {/* POST HERO IMAGE */}
                {/* Prismic Issue => recently added field is not added to previous documents */}
                {(getPostData()?.data?.show_hero === null || getPostData()?.data?.show_hero) && (
                    <>
                        {pageContext?.isAmp ? (
                            <div
                                className={styles['background_amp_hero']}
                                style={{ backgroundImage: `url(${getPostData()?.data?.hero_img?.url})` }}
                            />
                        ) : (
                            <div className={styles['hero_img_wrapper']}>
                                <Image
                                    {...getPostData()?.data?.hero_img}
                                    style={{ objectFit: 'cover' }}
                                    className={styles['hero_img']}
                                />
                            </div>
                        )}
                        {getPostData()?.data?.hero_img?.alt && (
                            <span className={styles['hero_img_caption']}>{getPostData()?.data?.hero_img?.alt}</span>
                        )}
                    </>
                )}

                <div className={styles['post_content']}>
                    <div className={styles['main_content']}>
                        {getPostData()?.data?.body.map(({ primary, id, slice_type }: any) => {
                            if (slice_type === 'content') {
                                return <ContentSlice {...primary} key={id} isAmp={pageContext?.isAmp} />;
                            }
                            if (slice_type === 'interviewee') {
                                return <IntervieweeSlice {...primary} key={id} isAmp={pageContext?.isAmp} />;
                            }
                        })}

                        {/* author card */}
                        {Object.keys(authorData).find((e) => e === getPostData()?.data?.author) && (
                            <div className={styles['author_card']}>
                                <span>{t('authors.span')}</span>
                                <BorderCard
                                    name={
                                        getPostData()?.data?.author === '日本ドッグサップ協会' ? (
                                            <a
                                                href="https://dsaj.spes-activity-nasu.com/"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {getPostData()?.data?.author}
                                            </a>
                                        ) : (
                                            getPostData()?.data?.author
                                        )
                                    }
                                    job_title={
                                        <Trans
                                            i18nKey={`authors.${getPostData()?.data?.author}.job_title`}
                                            components={{
                                                link1: transLink(authorData[getPostData()?.data?.author]?.url?.[0]),
                                                link2: transLink(authorData[getPostData()?.data?.author]?.url?.[5]),
                                            }}
                                        />
                                    }
                                    description={
                                        <Trans
                                            i18nKey={`authors.${getPostData()?.data?.author}.description`}
                                            components={{
                                                link2: transLink(authorData?.[getPostData()?.data?.author]?.url?.[1]),
                                                link3: transLink(authorData?.[getPostData()?.data?.author]?.url?.[2]),
                                                link4: transLink(authorData?.[getPostData()?.data?.author]?.url?.[3]),
                                                link5: transLink(authorData?.[getPostData()?.data?.author]?.url?.[4]),
                                            }}
                                        />
                                    }
                                    image={authorData[getPostData()?.data?.author]?.image}
                                    isAmp={pageContext?.isAmp}
                                />
                            </div>
                        )}

                        {getPostData()?.data?.show_contact && <SnsBanner />}

                        {/* hide this banner on mobile */}
                        {getPostData()?.data?.show_post_banner && (
                            <PostBanner
                                className={styles['main_content_post_banner']}
                                uid={pageContext?.uid}
                                isAmp={pageContext?.isAmp}
                            />
                        )}
                    </div>

                    {/* POST SIDE BAR */}
                    <SideBar>
                        {getPostData()?.data?.show_side_banner && <SideBanner {...pageContext} />}
                        <RecommendedPosts {...pageContext} />
                    </SideBar>
                </div>

                {/* display this banner on mobile */}
                {getPostData()?.data?.show_post_banner && (
                    <PostBanner className={styles['side_bar_post_banner']} {...pageContext} />
                )}
                <NewPosts {...pageContext} />
            </div>
        </>
    );
};

export const query = graphql`
    query PostTemplateQuery($uid: String) {
        prismicPost(uid: { eq: $uid }) {
            uid
            last_publication_date
            first_publication_date
            data {
                hero_img {
                    url
                    alt
                    dimensions {
                        height
                        width
                    }
                    localFile {
                        childImageSharp {
                            gatsbyImageData(width: 900, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF], quality: 70)
                        }
                    }
                }
                title {
                    text
                }
                title_color
                author
                category
                date
                show_hero
                show_side_banner
                show_contact
                show_post_banner
                meta_description {
                    text
                }
                body {
                    ... on PrismicPostBodyInterviewee {
                        primary {
                            interviewee
                        }
                        id
                        slice_type
                    }
                    ... on PrismicPostBodyContent {
                        primary {
                            text {
                                raw
                                text
                            }
                            text_color
                        }
                        id
                        slice_type
                    }
                }
            }
        }
    }
`;

export default PostTemplate;
